import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { useAuth } from "../../../contexts/AuthContext";
import "./TablesPrint.css";

const API = process.env.REACT_APP_API_BASE;

export default function TablesPrint() {
  const { wedding: authWedding } = useAuth();
  const [tables, setTables] = useState([]);
  const [groups, setGroups] = useState([]);
  const [weddingInfo, setWeddingInfo] = useState(authWedding || null);
  const [profileInfo, setProfileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [tRes, gRes, wRes, pRes] = await Promise.all([
          fetchWithAuth(`${API}/get-tables`, { method: "POST" }),
          fetchWithAuth(`${API}/guests-grouped`, { method: "POST" }),
          fetchWithAuth(`${API}/weddings/my`, { method: "GET" }).catch(() => null),
          fetchWithAuth(`${API}/users/{user_id}`, { method: "GET" }).catch(() => null),
        ]);

        if (!tRes?.ok) throw new Error("Failed to fetch tables");
        if (!gRes?.ok) throw new Error("Failed to fetch guests");

        const tData = await tRes.json();
        const gData = await gRes.json();
        setTables(Array.isArray(tData) ? tData : []);
        setGroups(Array.isArray(gData) ? gData : []);
        if (wRes?.ok) {
          const wData = await wRes.json();
          setWeddingInfo(wData);
        } else if (authWedding) {
          setWeddingInfo(authWedding);
        }
        if (pRes?.ok) {
          const pData = await pRes.json();
          setProfileInfo(pData);
        }
      } catch (e) {
        console.error(e);
        setError("Грешка при вчитување на податоци за печатење.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authWedding]);

  const tableAssignments = useMemo(() => {
    const map = new Map();
    tables.forEach((t) => map.set(t.number, []));

    groups.forEach((groupRaw) => {
      const group = Array.isArray(groupRaw) ? groupRaw : [];
      if (!group.length) return;
      const mainGuest = group.find((g) => g.is_main_guest) || group[0];
      const tableNum = mainGuest?.table_number ?? group.find((g) => g.table_number)?.table_number;
      if (!tableNum) return;
      const list = map.get(tableNum);
      if (!list) return;

      const names = [];
      if (mainGuest?.name || mainGuest?.last_name) {
        names.push(`${mainGuest.name ?? ""} ${mainGuest.last_name ?? ""}`.trim());
      }
      group
        .filter((g) => !g.is_main_guest)
        .forEach((g) => {
          if (g?.name || g?.last_name) {
            names.push(`${g.name ?? ""} ${g.last_name ?? ""}`.trim());
          }
        });

      map.set(tableNum, [...list, ...names]);
    });

    // Sort names within each table
    for (const [k, arr] of map.entries()) {
      map.set(
        k,
        arr
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      );
    }

    return map;
  }, [tables, groups]);

  const coupleLine = useMemo(() => {
    const groomFirst = (weddingInfo?.groom_name || profileInfo?.groom_name || "").trim();
    const groomLast = (profileInfo?.groom_last_name || "").trim();
    const brideFirst = (weddingInfo?.bride_name || profileInfo?.bride_name || "").trim();
    const brideLast = (profileInfo?.bride_last_name || "").trim();

    const groom = [groomFirst, groomLast].filter(Boolean).join(" ").trim();
    const bride = [brideFirst, brideLast].filter(Boolean).join(" ").trim();

    if (bride && groom) return `${groom} & ${bride}`;
    if (groom || bride) return groom || bride;
    return "План на маси";
  }, [weddingInfo, profileInfo]);

  const weddingDateText = useMemo(() => {
    if (!weddingInfo?.date) return "";
    const date = new Date(weddingInfo.date);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("mk-MK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, [weddingInfo]);

  if (loading) {
    return (
      <Box className="tables-print-container" sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
        <Typography mt={2}>Вчитување...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="tables-print-container" sx={{ textAlign: "center", mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const tablesWithGuests = tables
    .filter((t) => Array.isArray(tableAssignments.get(t.number)) && tableAssignments.get(t.number).length > 0)
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  return (
    <Box className="tables-print-container">
      <header className="tables-print-heading">
        <h1>{coupleLine}</h1>
        {weddingDateText && <p>{weddingDateText}</p>}
      </header>

      <div className="print-actions no-print">
        <Button
          variant="contained"
          onClick={() => window.print()}
          sx={{
            backgroundColor: 'var(--primary-color)',
            '&:hover': { backgroundColor: 'var(--primary-color-dark)' }
          }}
        >
          Печати
        </Button>
      </div>

      <div className="tables-print-grid">
        {tablesWithGuests.map((table) => {
          const names = tableAssignments.get(table.number) || [];

          return (
            <article className="table-visual-card" key={table.id || table.number}>
              <div className="table-print-header">
                <h3 className="table-print-title">Маса {table.number}</h3>
                <span className="table-print-count">{names.length} гости</span>
              </div>
              <ul className="table-print-guest-list">
                {names.map((fullName, idx) => (
                  <li key={`${table.number}-${idx}`}>{fullName}</li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>
    </Box>
  );
}
